/**
 * Authentication types and the role hierarchy.
 *
 * Separate from the context so non-component modules — route guards, tests —
 * can import the role logic without pulling in React.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.13, PRD §4
 */
import type { Role, SessionUser } from '@nexus/contracts';

export type { Role, SessionUser };

/**
 * Role ranks (PRD §4).
 *
 * Admin includes everything an Editor can do, and Editor everything a Viewer
 * can. Ranking them means a check reads "at least Editor" rather than listing
 * every role that qualifies — a list that is wrong the moment a role is added.
 */
const ROLE_RANK: Record<Role, number> = {
  VIEWER: 1,
  EDITOR: 2,
  ADMIN: 3,
};

/**
 * True when `role` meets or exceeds `required`.
 *
 * This is a convenience for the interface only. PRD §6.2 is explicit that
 * authorisation is enforced server-side; hiding a button the API would refuse
 * is courtesy, not security.
 */
export function hasRole(role: Role | null | undefined, required: Role): boolean {
  if (role === null || role === undefined) return false;
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

/** The authenticated session, or its absence. */
export interface AuthState {
  user: SessionUser | null;
  /** True until the initial session check has completed. */
  isLoading: boolean;
}
